/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import TourPanel from "./TourPanel";
import "./GuidedTour.css";

// How far the spotlight ring stands off the element it is drawn around, and
// how far the tooltip sits from that ring. Both in px.
const PAD = 6;
const GAP = 14;

// The tooltip's own width, which has to be known here rather than only in the
// stylesheet: the placement arithmetic below needs it before the card exists.
const CARD = 380;

// Keeps the card off the very edge of the viewport when the anchor is near it.
const EDGE = 16;

// The arrow's box: 7px transparent borders on every side, one of them
// coloured, so the square is 14 and the visible apex is half that in.
const ARROW = 14;

// Only a starting guess at the card's height, used for the single render
// before the real one is known. Everything after that measures: the card has
// no max-height and its body varies by step, and a guess that runs high makes
// the clamp reserve room the card does not need -- which pins it near the top
// of the page while the ring scrolls on underneath.
const CARD_H = 360;

/**
 * Nonprofit · the guided tour (CP-308).
 *
 * A layer over the workspace: a dimmed page, one element lit up through it,
 * and a card pointing at that element. What each step says and points at is
 * TOUR_STEPS in data.js; this only draws whatever the view model hands it.
 *
 * The whole page behind is inert while the tour is up, the lit element
 * included. A cutout that let the reader press the thing being described
 * sounds better than it is: half of these steps point at a control that
 * navigates, and a press on one strands the card pointing at an element that
 * is no longer on screen. The tour therefore describes those actions and
 * exposes only its own Next/Skip navigation until the layer is closed.
 */
class GuidedTour extends Component {
  constructor(props) {
    super(props);
    // Where the spotlit element is, in viewport coordinates. Null until the
    // first measurement, and for a step that has no target at all.
    this.state = { rect: null, cardH: 0 };
    // The card itself, so its height can be measured rather than assumed.
    this.card = React.createRef();
    this.raf = 0;
    // The anchor this instance has already scrolled to. Most of the steps
    // point at something below the fold of Impact & proof, so the target has
    // to be brought into view -- but exactly once per step, or the layer
    // would fight the reader every time they scrolled to look at the page
    // the card is describing.
    this.scrolledFor = null;
    // The final top-edge correction is also once per step. Without this
    // separate guard, every user scroll that moved a start-aligned anchor
    // above the viewport was mistaken for unfinished initial alignment and
    // immediately pulled the page back up.
    this.alignedFor = null;
  }

  componentDidMount() {
    this.measure();
    this.measureCard();
    // The anchor can arrive or leave after the step does. Impact & proof
    // fetches its sections, so the first visit can measure before the target
    // exists -- and the reverse happens too, when a fetch lands and hides the
    // add button the ring was drawn around. Watching the tree costs one
    // observer for as long as the tour is up and removes both cases; without
    // it the step either never gets its spotlight or keeps one around empty
    // space until an unrelated scroll happens to correct it.
    if (typeof window.MutationObserver === "function") {
      this.observer = new window.MutationObserver(this.remeasure);
      this.observer.observe(document.body, { childList: true, subtree: true });
    }
    document.addEventListener("keydown", this.onKey);
    // Capture, because the scroll that moves the anchor is the content
    // column's rather than the window's — a listener on the window alone
    // never hears it and the ring drifts off the element.
    window.addEventListener("resize", this.remeasure);
    window.addEventListener("scroll", this.remeasure, true);
  }

  componentDidUpdate(prev) {
    // A new step points at a new element, so the old rect must not be drawn
    // against it for even one frame.
    if (prev.vm.tour.anchor !== this.props.vm.tour.anchor) {
      this.measure();
    }
    // The panel's copy, and therefore its height, changes only with the step.
    // Measuring after every update made the measurement's own setState feed
    // straight back into componentDidUpdate; small layout oscillations could
    // then hit React's maximum update depth. A new step gets one correcting
    // measurement after its new panel has rendered.
    if (prev.vm.tour.id !== this.props.vm.tour.id) {
      this.measureCard();
    }
  }

  /**
   * The card's real height, which decides how much room a side needs.
   *
   * Read from the DOM because it cannot be known any other way: the card has
   * no fixed height, and its body is a different length on every step. Only
   * written back when it actually changes, or this would re-render forever.
   */
  measureCard() {
    const node = this.card.current;
    if (!node) {
      return;
    }
    const height = node.offsetHeight;
    if (height && height !== this.state.cardH) {
      this.setState({ cardH: height });
    }
  }

  // What the placement arithmetic should treat as the card's height: measured
  // once there is a card to measure, and the constant for the first render.
  cardHeight() {
    return this.state.cardH || CARD_H;
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.remeasure);
    window.removeEventListener("scroll", this.remeasure, true);
    document.removeEventListener("keydown", this.onKey);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.raf) {
      window.cancelAnimationFrame(this.raf);
    }
  }

  /**
   * Escape leaves the tour, the way it closes an (i) panel.
   *
   * The blocking layer swallows every press on the workspace behind it, so
   * without this a reader who cannot use a pointer has no way out of a run
   * that starts by itself on first sign-in. Routed through whichever exit the
   * step offers rather than a second path to the same place, so the run is
   * still stamped as seen.
   */
  onKey = (event) => {
    if (event.key !== "Escape" && event.key !== "Esc") {
      return;
    }
    const buttons = (this.props.vm.tour || {}).buttons || [];
    const exit =
      buttons.find((button) => button.id === "skip") ||
      buttons.find((button) => button.id === "later") ||
      buttons.find((button) => button.id === "finish");
    if (exit && typeof exit.press === "function") {
      event.preventDefault();
      exit.press();
    }
  };

  /**
   * Reads the anchor's box.
   *
   * Deferred by a frame on purpose. Advancing a step can change the section
   * on screen, and the element this step points at may be part of what that
   * render puts there — measured synchronously it is either absent or still
   * at its pre-layout position, and the ring lands in the wrong place.
   */
  measure = () => {
    if (this.raf) {
      window.cancelAnimationFrame(this.raf);
    }
    this.raf = window.requestAnimationFrame(() => {
      this.raf = 0;
      this.read();
    });
  };

  // The same read, without the frame's delay — resize and scroll are already
  // firing after layout, and deferring them makes the ring lag the page.
  remeasure = () => this.read();

  read = () => {
    const anchor = this.props.vm.tour.anchor;
    if (!anchor) {
      if (this.state.rect) {
        this.setState({ rect: null });
      }
      return;
    }
    const node = document.querySelector('[data-tour="' + anchor + '"]');
    if (!node) {
      // A step whose target is not on the page falls back to the centred
      // layout rather than drawing a ring around nothing. That is the right
      // outcome for a section that has not loaded its rows yet, and it is
      // also what stops a mistyped anchor taking the tour down.
      if (this.state.rect) {
        this.setState({ rect: null });
      }
      return;
    }
    // A very tall spotlight can nominate a smaller element to scroll by. The
    // ring still measures `node`; only viewport alignment uses this compact
    // anchor, so more of the large highlighted region remains visible.
    const scrollNode =
      document.querySelector('[data-tour-scroll="' + anchor + '"]') || node;
    // Once per step, before measuring: a ring around an element two screens
    // down is a ring nobody sees. scrollIntoView is guarded because jsdom
    // does not implement it, and a tour that throws in the tests is worse
    // than one that does not scroll in them.
    if (this.scrolledFor !== anchor) {
      this.scrolledFor = anchor;
      if (typeof scrollNode.scrollIntoView === "function") {
        scrollNode.scrollIntoView({
          block: this.props.vm.tour.scrollBlock || "center",
          inline: "nearest",
        });
      }
      // Measured on the next frame instead of now, so the box read is the
      // one the scroll left behind rather than the one before it.
      this.measure();
      return;
    }
    const box = node.getBoundingClientRect();
    const scrollBox = scrollNode.getBoundingClientRect();
    // `scrollIntoView({ block: "start" })` can leave the top of a target just
    // above the viewport while a smooth page scroll and this overlay's own
    // measurement settle together. Correct that final few pixels so a step
    // that explicitly asks for start alignment never loses the heading it is
    // introducing. The size guard keeps jsdom's all-zero boxes out of this
    // browser-only path.
    const shouldAlign = this.alignedFor !== anchor;
    this.alignedFor = anchor;
    if (
      shouldAlign &&
      this.props.vm.tour.scrollBlock === "start" &&
      scrollBox.top < EDGE &&
      (scrollBox.width || scrollBox.height) &&
      typeof window.scrollBy === "function"
    ) {
      window.scrollBy({
        top: scrollBox.top - EDGE,
        left: 0,
        behavior: "auto",
      });
      this.measure();
      return;
    }
    const rect = {
      top: box.top - PAD,
      left: box.left - PAD,
      width: box.width + PAD * 2,
      height: box.height + PAD * 2,
    };
    const was = this.state.rect;
    if (
      !was ||
      was.top !== rect.top ||
      was.left !== rect.left ||
      was.width !== rect.width ||
      was.height !== rect.height
    ) {
      this.setState({ rect: rect });
    }
  };

  /**
   * The side the card actually goes on, which is not always the one the step
   * asked for.
   *
   * A step names a side that suits the element it was written against --
   * "left" for the goal step, because its anchor is a compact pill that
   * `margin-left: auto` pushes to the right of its row, leaving the whole
   * column free on the left. But an anchor is not always the one a step was
   * drafted against: that same step falls back to the full-width section head
   * when the add button is hidden, and a full-width head starts at the left
   * edge of the column with no room beside it at all.
   *
   * So the asked-for side is honoured when the card fits and abandoned when it
   * does not. Flipping is better than clamping here -- a clamped card overlaps
   * the very element it is pointing at.
   */
  placeFor(rect, place) {
    if (place === "bottom" || place === "top") {
      // The same question vertically, and it bites hardest on a step whose
      // anchor has just been scrolled to the top of the page: "top" then has
      // nothing above it to put the card in, and the reader is left looking at
      // a ring with its instructions off the top of the screen.
      const height = window.innerHeight;
      const above = rect.top - GAP - EDGE;
      const below = height - (rect.top + rect.height) - GAP - EDGE;
      const card = this.cardHeight();
      if (place === "top" && above < card && below >= card) {
        return "bottom";
      }
      if (place === "bottom" && below < card && above >= card) {
        return "top";
      }
      return place;
    }
    const width = window.innerWidth;
    const roomLeft = rect.left - GAP - EDGE;
    const roomRight = width - (rect.left + rect.width) - GAP - EDGE;
    if (place === "left" && roomLeft < CARD && roomRight >= CARD) {
      return "right";
    }
    if (place === "right" && roomRight < CARD && roomLeft >= CARD) {
      return "left";
    }
    return place;
  }

  /**
   * Where the card goes, given the ring and which side the step asked for.
   *
   * Only the placements the catalogue uses are handled; anything else is
   * treated as "right", which is the one that suits a left-nav target. The
   * cross-axis position is clamped to the viewport so a target near the top
   * or bottom does not push the card off the screen.
   */
  cardStyle(rect, place, align) {
    const height = window.innerHeight;
    const width = window.innerWidth;
    if (place === "bottom" || place === "top") {
      const left = Math.min(
        Math.max(EDGE, rect.left + rect.width / 2 - CARD / 2),
        Math.max(EDGE, width - CARD - EDGE)
      );
      // Clamped for the case a flip cannot solve -- an anchor tall enough
      // that neither side has room -- so the card cannot leave the viewport
      // even then, against the card's measured height rather than a guess.
      const card = this.cardHeight();
      if (place === "bottom") {
        const wanted = rect.top + rect.height + GAP;
        return {
          top: Math.max(EDGE, Math.min(wanted, height - card - EDGE)),
          left: left,
        };
      }
      const wantedBottom = height - rect.top + GAP;
      return {
        bottom: Math.max(EDGE, Math.min(wantedBottom, height - card - EDGE)),
        left: left,
      };
    }
    // Vertically centred on the ring, then pulled back inside the viewport at
    // both ends, against the card's measured height. Measured rather than
    // assumed because assuming high makes this reserve room the card does not
    // need, which lifts it away from the ring it is pointing at.
    // Most compact controls look best with the panel centred on them. A tall
    // section can instead ask to align their top edges, keeping the panel by
    // the section heading rather than down beside an arbitrary midpoint.
    const wanted =
      align === "start" ? rect.top : rect.top + rect.height / 2 - 120;
    const lowest = Math.max(EDGE, height - this.cardHeight() - EDGE);
    const top = Math.max(EDGE, Math.min(wanted, lowest));
    // Clamped horizontally as well, for the case placeFor cannot solve: an
    // anchor wide enough that neither side has room. Without this the card
    // simply leaves the screen, which is what a full-width anchor on a step
    // asking for "left" used to do.
    if (place === "left") {
      const wantedRight = width - rect.left + GAP;
      return { top: top, right: Math.min(wantedRight, width - CARD - EDGE) };
    }
    const wantedLeft = rect.left + rect.width + GAP;
    return {
      top: top,
      left: Math.max(EDGE, Math.min(wantedLeft, width - CARD - EDGE)),
    };
  }

  /**
   * The little triangle on the card, pointing back at the ring. Positioned
   * against the ring rather than the card so it stays on the element even
   * once the clamp above has moved the card.
   *
   * Both axes, always. Setting only one left the other to resolve to its
   * static position -- which, for an absolutely positioned child whose every
   * preceding sibling is out of flow, is 0. So the triangle drew itself at
   * the very left edge of the viewport on the nine left/right steps and along
   * the top edge on the three others, never touching the ring it was meant to
   * point at.
   *
   * The element is a 14px square of transparent borders with one side
   * coloured, so its visible apex sits 7px in from the edge facing the ring.
   * Each case therefore offsets by ARROW / 2 to land that apex on the ring.
   */
  arrowStyle(rect, place) {
    const midY = rect.top + rect.height / 2 - ARROW / 2;
    const midX = rect.left + rect.width / 2 - ARROW / 2;
    if (place === "bottom") {
      return { left: midX, top: rect.top + rect.height - ARROW / 2 };
    }
    if (place === "top") {
      return { left: midX, top: rect.top - ARROW / 2 };
    }
    if (place === "left") {
      return { top: midY, left: rect.left - ARROW / 2 };
    }
    return { top: midY, left: rect.left + rect.width - ARROW / 2 };
  }

  render() {
    const tour = this.props.vm.tour;
    const rect = this.state.rect;
    // No anchor, or an anchor that is not on the page: the centred modal, over
    // a plain dim. This is step 1's own layout and every other step's
    // fallback, which is why the two share one branch.
    if (!rect) {
      return (
        <div className="Marketplace-Tour">
          <div className="Marketplace-Tour-scrim" />
          <div className="Marketplace-Tour-middle">
            <TourPanel panel={tour} />
          </div>
        </div>
      );
    }
    const place = this.placeFor(
      rect,
      tour.place === "center" ? "right" : tour.place
    );
    return (
      <div className="Marketplace-Tour">
        {/* One element does both jobs: the ring around the target, and — as a
            box-shadow wide enough to cover any viewport — the dim over
            everything else. Four separate scrim panels would leave seams at
            the corners and need re-laying out on every measurement. */}
        <div
          className="Marketplace-Tour-spot"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        />
        {/* Above the spotlight and below the card, so the page underneath —
            the lit element included — cannot be pressed. */}
        <div className="Marketplace-Tour-block" />
        <div
          className={"Marketplace-Tour-arrow Marketplace-Tour-arrow--" + place}
          style={this.arrowStyle(rect, place)}
        />
        <div
          className="Marketplace-Tour-anchored"
          ref={this.card}
          style={this.cardStyle(rect, place, tour.align)}
        >
          <TourPanel panel={tour} />
        </div>
      </div>
    );
  }
}

GuidedTour.propTypes = {
  vm: PropTypes.object.isRequired,
};

export default GuidedTour;
